// One deadline for every external request the map layers make (BBB-527).
//
// WHY THIS EXISTS. Neither government endpoint we query is ours, and neither
// request was bounded. A server that accepts the connection and then never
// answers, or answers with headers and then stalls part way through the body,
// left the Land characteristics panel and the soil legend spinning for the rest
// of the session. There is no retry and no cancel in front of it, so "forever"
// meant the farmer had to reopen the field.
//
// WHY 30 SECONDS. This is not a promptness target. The panel already says a
// layer is slow at 3 s and not available at 10 s (layerHealth.js), so all this
// number decides is when a request that is never going to settle gets abandoned.
// It therefore has to sit well beyond the slowest fetch that still SUCCEEDS, or
// it would start cutting off working layers on a weak signal, which is the worse
// failure of the two. A capped WFS page is up to a thousand polygons, several
// hundred kilobytes of GeoJSON, and the farmer reading it is usually standing in
// the field on one or two bars of rural mobile data rather than on farmhouse
// broadband. 30 s leaves that fetch room to finish and still bounds the hang.
//
// NOT MEASURED. We have no field timings for these endpoints on rural mobile
// data, so the number is a judgement, not a reading off a graph. If it turns out
// to be cutting working fetches short, raising it is the cheap fix.
export const EXTERNAL_FETCH_TIMEOUT_MS = 30000;

/**
 * fetch() a URL and read its body, both under one deadline.
 *
 * The body read is inside the deadline deliberately. AbortController can only
 * interrupt a stalled body if it is still live when the body is read, so a
 * helper that returned the Response and let the caller read it would bound the
 * headers and leave the expensive half unbounded.
 *
 * Returns the status rather than the Response so the body read stays in here. On
 * a failed response the body is not read at all: a caller only wants the status
 * for its own error message, and an error page is itself something that can
 * arrive slowly.
 *
 * @param {string} url
 * @param {object} [options] fetch options; `signal` is supplied here
 * @param {string} [label] what timed out, for the error message
 * @returns {Promise<{ok: boolean, status: number, body: string|null}>}
 */
export async function fetchTextWithTimeout(url, options = {}, label = "request") {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXTERNAL_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const body = response.ok ? await response.text() : null;
    return { ok: response.ok, status: response.status, body };
  } catch (err) {
    // An abort arrives as a DOMException named AbortError, which reads to a
    // caller as our own bug rather than as a dead server. Rethrown as a plain
    // Error so it travels the per-layer fail-open path in zoneLandData/index.js
    // exactly like an HTTP error, and shows up in the map layers as one more
    // failed fetch rather than as something nothing on the path expects.
    if (controller.signal.aborted) {
      throw new Error(`${label} timed out after ${EXTERNAL_FETCH_TIMEOUT_MS} ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
